import { Head } from '@inertiajs/react';
import { useState } from 'react';
import { FilterSidebar } from './components/FilterSidebar';
import MenteeProfile from './components/MenteeProfile';
import { DataTable, ColumnProps } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const REAPPLICANT_DAYS = 300;

interface Mentee {
  id: number;
  first_name: string;
  last_name: string;
  days_waiting: number;
}

interface Props {
  mentees: Mentee[];
  active_filters: Record<string, any>;
  filterable_questions: Array<{
    id: number;
    key: string;
    label: string;
    type: 'select' | 'multi_select' | 'radio' | 'string' | 'text';
    options?: Array<{ value: string; label: string }>;
  }>;
}

export default function MatchesIndex({
  mentees,
  active_filters,
  filterable_questions,
}: Props) {
  const [selectedMenteeId, setSelectedMenteeId] = useState<number | null>(null);

  const menteeColumns: ColumnProps<Mentee>[] = [
    {
      header: 'Name',
      render: (mentee) => `${mentee.first_name} ${mentee.last_name}.`,
    },
    {
      header: 'Waiting Time',
      render: (mentee) =>
        mentee.days_waiting > REAPPLICANT_DAYS ? (
          <Badge variant="secondary">Re-applicant</Badge>
        ) : (
          <span
            className={
              mentee.days_waiting > 14 ? 'text-orange-600 font-medium' : ''
            }
          >
            {mentee.days_waiting} days
          </span>
        ),
    },
    {
      header: 'Action',
      render: (mentee) => (
        <Button
          size="sm"
          variant="link"
          className="underline cursor-pointer"
          onClick={() => setSelectedMenteeId(mentee.id)}
        >
          View Application
        </Button>
      ),
    },
  ];

  return (
    <>
      <Head title="Browse Mentees" />

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Browse Mentees</h1>
          <p className="text-gray-600 mt-2">
            Find and connect with mentees who match your expertise
          </p>
        </div>

        {/* Filters at the top */}
        <FilterSidebar
          filterableQuestions={filterable_questions}
          activeFilters={active_filters}
        />

        {/* Results count */}
        <div className="mb-4 mt-6 text-sm text-muted-foreground">
          {mentees.length} results
        </div>

        {/* Mentee Table */}
        <DataTable
          data={mentees}
          columns={menteeColumns}
          emptyMessage="No mentees found matching your filters"
        />
      </div>

      {/* Mentee Profile Dialog */}
      <MenteeProfile
        menteeId={selectedMenteeId}
        onClose={() => setSelectedMenteeId(null)}
      />
    </>
  );
}
